import { useState, useCallback, useMemo } from 'react'
import { useAuthStore } from '@/stores/authStore'
import type { BdrSdr, Cadencia, BdrQueueGroups } from '@/types'
import { OPERATIONAL_BDR_ID, OPERATIONAL_BDR_NAME } from '@/utils/bdrPanelUtils'
import { processBdrQueueFila } from '@/utils/bdrPanelQueue'
import { useBdrPanelFetch } from '@/hooks/useBdrPanelFetch'
import { useBdrPanelActions } from '@/hooks/useBdrPanelActions'

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useBdrPanel() {
  const user = useAuthStore((s) => s.user)
  const fetch = useBdrPanelFetch(user?.id)

  const {
    currentBdrId,
    closers,
    partners,
    leadsList,
    setLeadsList,
    queueLeads,
    setQueueLeads,
    nurturingLeads,
    historyAgendamentos,
    stats,
    loading,
    isFiltering,
    loadingMore,
    loadingNurturing,
    loadingHistory,
    viewTab,
    setViewTab,
    page,
    hasMore,
    totalLeads,
    nurturingPage,
    nurturingHasMore,
    nurturingTotal,
    search,
    setSearch,
    filterStatus,
    setFilterStatus,
    filterTemp,
    setFilterTemp,
    filterFase,
    setFilterFase,
    sortField,
    setSortField,
    sortDir,
    setSortDir,
    nurturingSearch,
    setNurturingSearch,
    bankLeads,
    bankSearch,
    setBankSearch,
    bankFilterSegmento,
    setBankFilterSegmento,
    bankFilterPlataforma,
    setBankFilterPlataforma,
    bankFilterOrigem,
    setBankFilterOrigem,
    bankPage,
    bankTotalPages,
    bankSortField,
    setBankSortField,
    bankSortDir,
    setBankSortDir,
    loadingBank,
    fetchAllLeads,
    fetchTodayQueue,
    fetchNurturingLeads,
    fetchBankLeads,
    handleLoadMore,
  } = fetch

  const sdrs = useMemo<BdrSdr[]>(
    () => [{ id: OPERATIONAL_BDR_ID, name: OPERATIONAL_BDR_NAME }],
    [],
  )
  const setCurrentBdrId = useCallback(() => {
    /* carteira fixa (Sophia) — seletor desativado */
  }, [])

  const actions = useBdrPanelActions({
    currentBdrId,
    viewTab,
    closers,
    setLeadsList,
    setQueueLeads,
    fetchTodayQueue,
    fetchAllLeads,
    fetchBankLeads,
  })

  // ── Collapsible groups ───────────────────────────────────────────────────

  const [collapsed, setCollapsed] = useState({
    recebidos: false,
    ligacoes: false,
    followup: false,
    planejamento: false,
    reunioes: false,
  })

  const toggleGroup = useCallback((group: string) => {
    setCollapsed((prev) => ({ ...prev, [group]: !prev[group as keyof typeof prev] }))
  }, [])

  // ── getCadencia ──────────────────────────────────────────────────────────

  const getCadencia = useCallback(
    (leadId: string): Cadencia | null => {
      const fromQueue = queueLeads.find((l) => l.id === leadId)
      if (fromQueue?.cadencia) return fromQueue.cadencia
      const fromList = leadsList.find((l) => l.id === leadId)
      if (fromList?.cadencia) return fromList.cadencia
      const fromNurture = nurturingLeads.find((l) => l.id === leadId)
      if (fromNurture?.cadencia) return fromNurture.cadencia
      return null
    },
    [queueLeads, leadsList, nurturingLeads],
  )

  // ── processFila: group queue leads by cadence status ─────────────────────

  const processFila = useCallback(
    (tipo: string) => processBdrQueueFila(tipo, queueLeads, getCadencia),
    [queueLeads, getCadencia],
  )

  // ── queueGroups (memoized) ───────────────────────────────────────────────

  const queueGroups = useMemo<BdrQueueGroups>(
    () => ({
      recebidos: processFila('recebidos'),
      ligacoes: processFila('ligacoes'),
      followup: processFila('followup'),
      planejamento: processFila('planejamento'),
      reunioes: processFila('reunioes'),
    }),
    [processFila],
  )

  // ── filteredAllLeads ─────────────────────────────────────────────────────

  /** Lista completa do BDR (inclui leads com deal — aba "Todos os Leads"). */
  const filteredAllLeads = useMemo(() => leadsList, [leadsList])

  // ── Return ───────────────────────────────────────────────────────────────

  return {
    user,
    sdrs,
    operationalBdrName: OPERATIONAL_BDR_NAME,
    closers,
    partners,
    currentBdrId,
    setCurrentBdrId,
    stats,
    leadsList,
    nurturingLeads,
    historyAgendamentos,
    bankLeads,
    bankSearch,
    setBankSearch,
    bankFilterSegmento,
    setBankFilterSegmento,
    bankFilterPlataforma,
    setBankFilterPlataforma,
    bankFilterOrigem,
    setBankFilterOrigem,
    bankPage,
    bankTotalPages,
    bankSortField,
    setBankSortField,
    bankSortDir,
    setBankSortDir,
    loadingBank,
    fetchBankLeads,
    loading,
    isFiltering,
    loadingMore,
    loadingNurturing,
    loadingHistory,
    viewTab,
    setViewTab,
    collapsed,
    toggleGroup,
    page,
    hasMore,
    totalLeads,
    nurturingPage,
    nurturingHasMore,
    nurturingTotal,
    handleLoadMore,
    filtersAll: {
      page,
      search,
      setSearch,
      filterStatus,
      setFilterStatus,
      filterTemp,
      setFilterTemp,
      filterFase,
      setFilterFase,
      sortField,
      setSortField,
      sortDir,
      setSortDir,
    },
    filtersNurturing: {
      nurturingSearch,
      setNurturingSearch,
    },
    filteredAllLeads,
    queueGroups,
    getCadencia,
    fetchTodayQueue,
    fetchNurturingLeads,
    ...actions,
  }
}
